<template>
  <el-dialog class="batchDialog" :visible.sync="visible" width="1200px" :title="opts.title || '批量修改价格'" @close="closeHandle">
    <!-- 价格编辑表单 -->
    <EditPrice
      v-if="visible"
      ref="editPriceRef"
      :type="type"
      :platformType="opts.platformType"
      :priceMultiplierOptionsProp="priceMultiplierOptions"
      :defaultPriceMultiplier="defaultPriceMultiplier"
      @change="priceFormulaChange"
    />
    <!-- SKU价格表格 -->
    <div class="sku-table-container" v-if="tableList && tableList.length > 0">
      <el-table :data="tableList" border stripe max-height="400" class="sku-price-table">
        <el-table-column v-for="col in tableCols" :key="col.attrs.prop" v-bind="col.attrs">
          <template slot-scope="{ row }">
            <template v-if="col.type === 'input-digit'">
              <el-input-digit
                v-model.number="row[col.attrs.prop]"
                :placeholder="col.attrs.placeholder"
                style="width: 120px"
                @input="handlePriceInput(row)"
              />
              <div v-if="row.modifiedPriceError" style="color: red">{{ row.modifiedPriceError }}</div>
            </template>
            <span v-else>{{ row[col.attrs.prop] }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 对话框底部按钮 -->
    <span slot="footer" class="dialog-footer">
      <el-button :loading="requestLoading" @click="closeHandle">取 消</el-button>
      <el-button type="primary" :loading="requestLoading" @click="handleOK">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import EditPrice from '../../BasicComps/EditPrice.vue';
import { getPlatformDetailSkuPrice } from '@/api/prod/center';
import { cloneDeep } from 'lodash';

export default {
  components: {
    EditPrice,
  },
  data() {
    return {
      visible: false,
      opts: {},
      type: '',
      requestLoading: false,
      tableList: [], // 表格数据
      defaultPriceMultiplier: '',
      skuSpecificationsKey: 'skuSpecifications',
    };
  },
  computed: {
    tableCols() {
      const { specificationNames } = this.opts;
      const specsCols = specificationNames.map(name => ({
        attrs: {
          prop: name,
          label: name,
          width: '130',
          align: 'left',
        },
      }));
      const cols = [
        ...specsCols,
        {
          attrs: {
            prop: 'baseSellingPrice',
            label: '基本售价',
            width: '100',
            align: 'center',
            'header-tooltip': '商品的基础销售价，一般同销售价，可调整，用于供销商核定分销价',
          },
        },
        {
          attrs: {
            prop: 'salesPrice',
            label: '销售价',
            width: '100',
            align: 'center',
            'header-tooltip': '即商品零售价，出售单件商品标定的销售价格',
          },
        },
        {
          attrs: {
            prop: 'marketPrice',
            label: '市场价',
            width: '100',
            align: 'center',
            'header-tooltip': '即建议零售价，基于市场情况的指导价格',
          },
        },
        {
          attrs: {
            prop: 'purchasePrice',
            label: '成本价',
            width: '160',
            align: 'center',
            'header-tooltip': '生产或采购商品的价格',
          },
        },
        this.type === 'sales'
          ? {
              attrs: {
                prop: 'skuMultiPrice',
                label: '原拼单价',
                width: '120',
                align: 'center',
              },
            }
          : null,
        this.type === 'market'
          ? {
              attrs: {
                prop: 'skuMultiPrice',
                label: '拼单价',
                width: '120',
                align: 'center',
              },
            }
          : null,
        this.type === 'market'
          ? {
              attrs: {
                prop: 'skuPrice',
                label: '原单买价',
                width: '120',
                align: 'center',
              },
            }
          : null,
        {
          attrs: {
            prop: 'modifiedPrice',
            label: this.type === 'sales' ? '修改后拼单价' : '修改后单买价',
            width: '120',
            align: 'center',
          },
          type: 'input-digit',
          placeholder: '请输入价格',
        },
      ];
      return cols.filter(Boolean);
    },
    priceMultiplierOptions() {
      const baseOptions = [
        this.type === 'sales' ? { label: '原拼单价', value: 'skuMultiPrice' } : null,
        this.type === 'market' ? { label: '原单买价', value: 'skuPrice' } : null,
        this.type === 'market' ? { label: '拼单价', value: 'skuMultiPrice' } : null,
        { label: '基本售价', value: 'baseSellingPrice' },
        { label: '销售价', value: 'salesPrice' },
        { label: '市场价', value: 'marketPrice' },
        { label: '成本价', value: 'purchasePrice' },
      ];
      return baseOptions.filter(Boolean);
    },
  },
  methods: {
    method_open(opts) {
      this.opts = opts;
      this.visible = true;
      this.type = opts.type || 'sales';
      this.defaultPriceMultiplier = this.type === 'sales' ? 'skuMultiPrice' : 'skuPrice';
      this.getDetailSkuPrice();
    },
    async getDetailSkuPrice() {
      const skuList = this.opts.skuList.map(item => {
        return {
          baseSkuId: item.baseSkuId,
          specValue: item[this.skuSpecificationsKey].map(item => item.value).join(','),
          skuPrice: item.singlePrice || 0,
          skuMultiPrice: item.multiPrice || 0,
        };
      });
      const requestParams = {
        itemId: this.opts.baseItemId,
        skuList,
      };
      const res = await getPlatformDetailSkuPrice(requestParams);
      this.tableList = (res?.data || []).map(item => {
        const specValues = (item.specValue || '').split(',');
        this.opts.specificationNames.forEach((name, idx) => {
          item[name] = specValues[idx];
        });
        item.modifiedPrice = '';
        item.modifiedPriceError = '';
        return item;
      });
    },
    formatFormulaValue(formulaValue) {
      const { priceMultiplier, addValue, subtractionValue, centDime, percentage } = formulaValue;
      Object.assign(formulaValue, {
        priceMultiplier,
        addValue: addValue || 0,
        subtractionValue: subtractionValue || 0,
        centDime: centDime || 0,
        percentage: percentage || 0,
      });
      return formulaValue;
    },
    handlePriceInput(row) {
      if (this.type === 'market' && Number(row.modifiedPrice) < Number(row.skuMultiPrice) + 1) {
        row.modifiedPriceError = '需≥拼单价+1';
      } else {
        row.modifiedPriceError = '';
      }
    },
    async priceFormulaChange(formData) {
      if (!(await this.$refs.editPriceRef.validate())) return;

      const { way, inputValue, formulaValue } = formData;
      const { addValue, centDime, formula, percentage, priceMultiplier, subtractionValue } = formulaValue;
      this.tableList.forEach(item => {
        let modifiedPrice = 0;
        if (way === 'unity') {
          modifiedPrice = inputValue;
        } else if (way === 'formula') {
          // 基础公式计算
          modifiedPrice =
            (Number(item[priceMultiplier]) || 0) * ((Number(percentage) || 0) / 100) +
            (Number(addValue) || 0) -
            (Number(subtractionValue) || 0);

          // 根据分角处理选项修正
          const val = Number(modifiedPrice) || 0;
          switch (formula) {
            case 'reserve': // 保留角和分(四舍五入)
              modifiedPrice = Math.round(val * 100) / 100;
              break;
            case 'erase_cent_dime': // 抹去分和角
              modifiedPrice = Math.floor(val);
              break;
            case 'erase_cent': // 抹去分（保留一位小数，向下取整）
              modifiedPrice = Math.floor(val * 10) / 10;
              break;
            case 'unity': // 统一修改为xx.
              {
                const cents = Number(centDime) || 0; // 0-99
                const integer = Math.floor(val);
                modifiedPrice = integer + cents / 100;
              }
              break;
            default:
              modifiedPrice = Math.round(val * 100) / 100;
          }
        }
        item.modifiedPrice = modifiedPrice;
        this.handlePriceInput(item);
      });
    },
    handleOK() {
      if (this.tableList.find(item => item.modifiedPriceError)) {
        this.$message.error('存在不符合要求单买价，需修改后提交');
        return;
      }
      this.opts.okFn(cloneDeep(this.tableList));
      this.closeHandle();
    },
    closeHandle() {
      this.visible = false;
      Object.assign(this.$data, this.$options.data());
    },
  },
};
</script>

<style lang="scss" scoped>
.batchDialog {
  .sku-table-container {
    margin-top: 20px;

    .table-title {
      margin-bottom: 10px;
      color: #333;
      font-size: 14px;
      font-weight: 600;
    }

    .sku-price-table {
      width: 100%;

      ::v-deep .el-table__header {
        background-color: #f5f7fa;

        th {
          background-color: #f5f7fa !important;
          color: #606266;
          font-weight: 600;
        }
      }

      ::v-deep .el-table__body {
        tr:hover > td {
          background-color: #f5f7fa;
        }
      }

      .el-input {
        width: 100%;

        ::v-deep .el-input__inner {
          text-align: center;
          padding: 0 8px;
        }
      }
    }
  }

  .dialog-footer {
    text-align: center;
    margin-top: 20px;

    .el-button {
      min-width: 80px;
    }
  }
}
</style>